import {
  Box,
  Flex,
  Heading,
  Text,
  useBreakpointValue,
  VisuallyHidden,
  Button,
} from '@chakra-ui/react';
import {ArrowBackIcon, ArrowForwardIcon} from '@chakra-ui/icons';
import React, {useEffect, useRef} from 'react';
// https://react-slick.neostack.com/docs/api
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Slider from 'react-slick';
import mixpanel from 'mixpanel-browser';

interface SlickButton {
  children: React.ReactNode;
  onClick: () => void;
  arrowLeft?: boolean;
  enableAnimation?: boolean;
}

const SlickButton: React.FC<SlickButton> = ({
  children,
  onClick,
  arrowLeft,
  enableAnimation = false,
}) => {
  return (
    <Button
      variant="unstyled"
      color="white"
      fontSize={{base: '36px', md: '44px'}}
      height="44px"
      lineHeight={1}
      mx={{base: '10px', md: '30px'}}
      onClick={onClick}
      opacity={enableAnimation ? 1 : 0}
      transform={
        enableAnimation
          ? `translateX(0px)`
          : `translateX(${arrowLeft ? '-10' : '10'}px)`
      }
      transition="all .7s ease"
    >
      {children}
    </Button>
  );
};

interface SlickControlsProps {
  prev: () => void;
  next: () => void;
  enableAnimation?: boolean;
}

export const SlickControls: React.FC<SlickControlsProps> = ({
  enableAnimation,
  next,
  prev,
}) => {
  return (
    <>
      <SlickButton enableAnimation={enableAnimation} onClick={prev}>
        <VisuallyHidden>Previous</VisuallyHidden>
        <ArrowBackIcon />
      </SlickButton>

      <SlickButton enableAnimation={enableAnimation} onClick={next}>
        <VisuallyHidden>Next</VisuallyHidden>
        <ArrowForwardIcon />
      </SlickButton>
    </>
  );
};

export interface TimelineItemProps {
  date: string;
  title: string;
  content: string;
}

const TimelineItem: React.FC<
  TimelineItemProps & {enableAnimation: boolean; index: number}
> = ({date, title, content, enableAnimation, index}) => {
  const connectingLineBeforeStyle = useBreakpointValue({
    base: {},
    md: {
      content: '""',
      display: 'block',
      height: '2px',
      width: '100vw',
      background: 'gray.600',
      position: 'absolute',
      left: '50%',
      transform: 'translateY(14px)',
      zIndex: '-1',
    },
  });

  const connectingLineAfterStyle = useBreakpointValue({
    base: {
      content: '""',
      display: index !== 3 ? 'block' : 'none',
      height: 'calc(100% + 15px)',
      width: '2px',
      background: 'gray.600',
      position: 'absolute',
      top: '0',
      transform: 'translate(14px, 30px)',
      zIndex: '-1',
    },
    md: {
      content: '""',
      display: 'block',
      height: '2px',
      width: '100vw',
      background: 'gray.600',
      position: 'absolute',
      right: '50%',
      transform: 'translateY(14px)',
      zIndex: '-1',
    },
  });

  const timelineLabel = useBreakpointValue({
    base: index + 1,
    md: date,
  });

  return (
    <Box
      px={{base: '20px'}}
      py={{base: '16px', md: 0}}
      opacity={enableAnimation ? 1 : 0}
      transition="opacity 1s ease"
    >
      <Flex
        flexDirection={{base: 'row', md: 'column'}}
        alignItems="flex-start"
        height={{base: '100%', md: 'auto'}}
      >
        <Flex
          mb={{base: 0, md: '24px'}}
          mr={{base: '24px', md: 0}}
          position="relative"
          alignSelf="stretch"
          _before={{
            ...connectingLineBeforeStyle,
          }}
          _after={{
            ...connectingLineAfterStyle,
          }}
        >
          <Text
            as="span"
            fontSize="18px"
            lineHeight="30px"
            display="flex"
            justifyContent="center"
            alignItems="center"
            borderRadius="15px"
            background="#1FD3C5"
            height="30px"
            width={{base: '30px', md: 'auto'}}
            color="#011E31"
            zIndex={1}
            px={{base: 0, md: '15px'}}
          >
            {timelineLabel}
          </Text>
        </Flex>
        <Flex flexDirection="column" alignItems="center">
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignSelf="flex-start"
            height="30px"
            mb="8px"
            display={{base: 'flex', md: 'none'}}
          >
            <Text>{date}</Text>
          </Flex>
          <Heading size="md" as="h3" mb={{base: '4px', md: '8px'}}>
            {title}
          </Heading>
          <Text fontSize="16px">{content}</Text>
        </Flex>
      </Flex>
    </Box>
  );
};

interface TimelineProps {
  title: string;
  steps: TimelineItemProps[];
}

export const Timeline: React.FC<TimelineProps> = ({title, steps}) => {
  const parentRef = useRef<HTMLDivElement | null>(null);

  const slider = useRef(null);
  const settings = {
    infinite: false,
    centerMode: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
    variableWidth: true,
    arrows: false,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          rows: steps.length,
          slidesPerRow: 1,
          centerMode: false,
        },
      },
    ],
  };
  const cardWidth = useBreakpointValue(
    {base: '100vw', md: '340px'},
    {fallback: 'md'},
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line no-unused-expressions
    slider?.current?.slickNext();
  }, []);

  return (
    <Box
      as="section"
      ref={parentRef}
      position="relative"
      py={{base: '40px', md: '80px'}}
      color="white"
    >
      <Box>
        <Box maxW="670px" margin="0 auto" mb={{base: '30px', md: '48px'}}>
          <Heading size="lg" as="h2" textAlign="center">
            {title}
          </Heading>
        </Box>
        {steps?.length && (
          <Box pos="relative" width="100%" py={{base: 0, md: '40px'}}>
            <Box
              position="absolute"
              right="0px"
              top="-45px"
              display={{base: 'none', md: 'block'}}
            >
              <SlickButton
                enableAnimation
                onClick={() => {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  // eslint-disable-next-line no-unused-expressions
                  slider?.current?.slickPrev();
                  mixpanel.track('Timeline button click', {direction: 'back'});
                }}
              >
                <VisuallyHidden>Previous step</VisuallyHidden>
                <ArrowBackIcon />
              </SlickButton>

              <SlickButton
                enableAnimation
                onClick={() => {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  // eslint-disable-next-line no-unused-expressions
                  slider?.current?.slickNext();
                  mixpanel.track('Timeline button click', {
                    direction: 'forwards',
                  });
                }}
              >
                <VisuallyHidden>Next step</VisuallyHidden>
                <ArrowForwardIcon />
              </SlickButton>
            </Box>
            <Box width="100%">
              <Slider ref={slider} {...settings}>
                {steps.map((step, index) => (
                  <div key={step.title} style={{width: cardWidth}}>
                    <TimelineItem
                      enableAnimation
                      index={index}
                      date={step.date}
                      content={step.content}
                      title={step.title}
                    />
                  </div>
                ))}
              </Slider>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
